import { type FunctionComponent, lazy, Suspense } from 'react';
import { graphql, createFragmentContainer } from 'react-relay/legacy.js';
import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { type SharedFAQAuthor_author$data } from './__generated__/SharedFAQAuthor_author.graphql.ts';
import dibsCss from 'dibs-css';
import classnames from 'classnames';
import styles from './SharedFAQAuthor.module.css';
import { useClientState } from 'dibs-react-hooks/exports/useClientState.ts';

const Logo2020 = lazy(
    () =>
        import(
            /* webpackChunkName: "BuySharedFAQAuthorLogo2020" */ 'dibs-icons/exports/legacy/Logo2020.ts'
        )
);

type Props = {
    author: SharedFAQAuthor_author$data | null | undefined;
    formattedCreatedDate?: string | null;
};

const SharedFAQAuthorComponent: FunctionComponent<Props> = ({ author, formattedCreatedDate }) => {
    const isClient = useClientState();

    const sellerPreferences = author?.sellerPreferences;
    const imageClassNames = classnames(
        styles.image,
        dibsCss.objectContain,
        dibsCss.roundedFull,
        dibsCss.mrSmall
    );
    if (sellerPreferences) {
        const { label: name, scrollLogo: logo, urlLabel } = sellerPreferences;
        if (name) {
            const url = `/dealers/${urlLabel}/`;
            return (
                <div className={classnames(dibsCss.flex, dibsCss.itemsCenter, dibsCss.mbSmall)}>
                    {logo && (
                        <img
                            className={classnames(imageClassNames, dibsCss.bgWhite)}
                            src={logo}
                            alt={name}
                        />
                    )}
                    <div
                        className={classnames(dibsCss.flex, dibsCss.flexCol)}
                        data-tn="faq-answer-author"
                    >
                        <Link href={url}>{name}</Link>
                        {formattedCreatedDate && <span>{formattedCreatedDate}</span>}
                    </div>
                </div>
            );
        }
    }

    return (
        <div className={classnames(dibsCss.flex, dibsCss.itemsCenter, dibsCss.mbSmall)}>
            {isClient && (
                <Suspense fallback="">
                    <Logo2020
                        className={classnames(
                            imageClassNames,
                            dibsCss.bgPitchblack,
                            dibsCss.fillWhite,
                            dibsCss.pXsmall
                        )}
                    />
                </Suspense>
            )}

            <div className={classnames(dibsCss.flex, dibsCss.flexCol)} data-tn="faq-answer-author">
                <FormattedMessage
                    id="SharedFAQ.author.defaultAuthor"
                    defaultMessage="1stDibs Expert"
                />
                {formattedCreatedDate && <span>{formattedCreatedDate}</span>}
            </div>
        </div>
    );
};

export const SharedFAQAuthor = createFragmentContainer(SharedFAQAuthorComponent, {
    author: graphql`
        fragment SharedFAQAuthor_author on Seller {
            sellerPreferences {
                label
                urlLabel
                scrollLogo
            }
        }
    `,
});
